import { useId, useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  mergeClasses,
} from '@coreai/fluentui-react';
import {
  ChevronRightRegular,
  ChevronDownRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  SubtractCircleRegular,
} from '@fluentui/react-icons';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { StageEventSnapshot, PipelineStageId, StageStepId } from '@/types/agui';
import { getStatusIcon } from './statusIcons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    width: '100%',
  },
  stageRow: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: tokens.borderRadiusSmall,
    overflow: 'hidden',
    width: '100%',
  },
  stageRowActive: {
    borderLeft: `2px solid ${tokens.colorBrandStroke1}`,
    paddingLeft: tokens.spacingHorizontalXS,
  },
  stageRowFailed: {
    borderLeft: `2px solid ${tokens.colorPaletteRedBorder1}`,
    paddingLeft: tokens.spacingHorizontalXS,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    width: '100%',
    textAlign: 'left',
    color: 'inherit',
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '-2px',
    },
  },
  headerClickable: {
    cursor: 'pointer',
  },
  chevronWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  stageIcon: {
    display: 'inline-flex',
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    flexShrink: 0,
  },
  summary: {
    flexGrow: 1,
    minWidth: 0,
    wordBreak: 'break-word',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandStroke1,
    flexShrink: 0,
    animationName: {
      from: { opacity: 0.35 },
      to: { opacity: 1 },
    },
    // Use the motion-token "slower" duration (~500ms) doubled by alternate
    // direction → ~1s round trip — close to the prior 1.2s but driven by a
    // design-system token rather than a hard-coded value.
    animationDuration: tokens.durationUltraSlow,
    animationIterationCount: 'infinite',
    animationDirection: 'alternate',
    animationTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      // Honor the user's OS-level preference: hold a stable dim opacity
      // (still visible as "in flight") instead of pulsing.
      animationIterationCount: 0,
      animationName: 'none',
      opacity: 0.7,
    },
  },
  body: {
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  eventList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    listStyleType: 'none',
    margin: 0,
    padding: 0,
  },
  eventItem: {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalXS,
  },
  eventIcon: {
    display: 'inline-flex',
    flexShrink: 0,
  },
  eventText: {
    minWidth: 0,
    wordBreak: 'break-word',
  },
  statusSlot: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '16px',
    height: '16px',
    flexShrink: 0,
    marginLeft: 'auto',
  },
  visuallyHidden: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
  },
});

interface TurnStagesPanelProps {
  /** Stages to render, in execution order. */
  stages: StageEventSnapshot[];
  /** When true the most-recent in-flight stage shows the active indicator. */
  live: boolean;
}

/**
 * One stage card per row, rendered as a collapsible disclosure. All cards start
 * collapsed; the user expands the ones they want to inspect.
 */
export const TurnStagesPanel = ({ stages, live }: TurnStagesPanelProps) => {
  const styles = useStyles();
  // Per-panel id prefix so the disclosure aria-controls/id pairs are unique
  // even when multiple TurnStagesPanel instances exist on screen at once
  // (one per assistant turn). Without this, screen readers would see
  // duplicate ids like "stage-body-plan" tied to every assistant message.
  const panelId = useId();

  // Determine which stage is active (the most-recent non-terminal one).
  const activeIndex = useMemo(() => {
    if (!live || stages.length === 0) return -1;
    for (let i = stages.length - 1; i >= 0; i--) {
      const step = stages[i]?.step;
      if (step === 'started' || step === 'progress') return i;
    }
    return -1;
  }, [stages, live]);

  const [manuallyToggled, setManuallyToggled] = useState<Map<string, boolean>>(new Map());

  if (stages.length === 0) {
    return null;
  }

  const isExpanded = (stage: PipelineStageId): boolean => manuallyToggled.get(stage) ?? false;

  const toggle = (stage: PipelineStageId) => {
    const current = isExpanded(stage);
    setManuallyToggled((prev) => {
      const next = new Map(prev);
      next.set(stage, !current);
      return next;
    });
  };

  return (
    <div className={styles.container} role="list" aria-label="Pipeline stages">
      {stages.map((s, i) => {
        const expanded = isExpanded(s.stage);
        const isActive = i === activeIndex;
        const isFailed = s.step === 'failed';
        const rowClass = mergeClasses(
          styles.stageRow,
          isActive && styles.stageRowActive,
          isFailed && styles.stageRowFailed,
        );
        const events = s.events ?? [];
        const hasEvents = events.length > 0;
        const isTerminal = s.step === 'completed' || s.step === 'failed' || s.step === 'skipped';
        const headerText = s.summary?.trim() || s.stage;
        const showDetails = !!s.details && isTerminal;
        const showEvents = hasEvents && !showDetails;
        const hasBody = showEvents || showDetails;
        return (
          <div key={s.stage} className={rowClass} role="listitem">
            <div
              id={`${panelId}-${s.stage}-header`}
              className={mergeClasses(styles.header, hasBody && styles.headerClickable)}
              {...(hasBody ? {
                role: 'button',
                tabIndex: 0,
                onClick: () => toggle(s.stage),
                onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(s.stage); } },
                'aria-expanded': expanded,
                'aria-controls': `${panelId}-${s.stage}-body`,
              } : {})}
            >
              {hasBody && (
                <span aria-hidden="true" className={styles.chevronWrapper}>
                  {expanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                </span>
              )}
              {s.emoji && <span className={styles.stageIcon}>{getStatusIcon(s.emoji)}</span>}
              <Text size={200} className={styles.summary}>
                {headerText}
              </Text>
              <span className={styles.statusSlot}>
                {isActive
                  ? <>
                      <span className={styles.dot} aria-hidden="true" />
                      <span className={styles.visuallyHidden}>Active</span>
                    </>
                  : <StageStepBadge step={s.step} />}
              </span>
            </div>
            {expanded && hasBody && (
              <div
                className={styles.body}
                id={`${panelId}-${s.stage}-body`}
                role="region"
                aria-labelledby={`${panelId}-${s.stage}-header`}
              >
                {showEvents && (
                  <ul className={styles.eventList}>
                    {events.map((ev, evIdx) => (
                      <li key={`${ev.summary}-${ev.timestamp}-${evIdx}`} className={styles.eventItem}>
                        {ev.emoji && <span className={styles.eventIcon}>{getStatusIcon(ev.emoji)}</span>}
                        <span className={styles.eventText}>{ev.summary}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {showDetails && (
                  <Markdown remarkPlugins={[remarkGfm]}>{s.details}</Markdown>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const useBadgeStyles = makeStyles({
  badge: {
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  visuallyHidden: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
  },
});

function StageStepBadge({ step }: { step: StageStepId }) {
  const styles = useBadgeStyles();
  const icon = step === 'completed'
    ? <CheckmarkCircleRegular aria-hidden="true" />
    : step === 'failed'
      ? <DismissCircleRegular aria-hidden="true" />
      : step === 'skipped'
        ? <SubtractCircleRegular aria-hidden="true" />
        : null;
  if (!icon) return null;
  return (
    <span className={styles.badge}>
      {icon}
      <span className={styles.visuallyHidden}>{step}</span>
    </span>
  );
}
